import { useEffect, useMemo, useState } from 'react';
import { STEPS, stepsOrder } from '@app/constants/stages';
import { Modal } from '@app/components/common/Modal/Modal';
import { useLocation, useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import { IMockTestUserStart, mockTestStart } from '@app/api/mockTest.api';
import { CheckHeadset } from './CheckHeadset';
import { CheckMicrophone } from './CheckMicrophone';
import { CheckKeyboard } from './CheckKeyboard';
import * as S from './ExamMockTestIntro.styles';
import { Button } from 'antd';
import { Structure } from './Structure';
import { Introduction } from './Introduction';
import { Description } from './Description';
import { Note } from './Note';
import { useTranslation } from 'react-i18next';
import { MOCK_TEST_TYPE } from '@app/constants/mockTest';
import { getInitialStepAndTime } from '@app/utils/utils';

type StepConfig = {
  Component: React.FC<any>;
};

const stepsConfig: Record<string, StepConfig> = {
  [STEPS.STRUCTURE]: { Component: Structure },
  [STEPS.CHECK_HEADSET]: { Component: CheckHeadset },
  [STEPS.CHECK_MICROPHONE]: { Component: CheckMicrophone },
  [STEPS.CHECK_KEYBOARD]: { Component: CheckKeyboard },
  [STEPS.INTRODUCTION]: { Component: Introduction },
  [STEPS.DESCRIPTION]: { Component: Description },
  [STEPS.NOTE]: { Component: Note },
};

export const ExamMockTestIntro: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mockTestId = queryParams.get('id');
  const mockTestType = queryParams.get('type');
  const mockTestSection = queryParams.get('section');
  const [stepIndex, setStepIndex] = useState(0);
  const [isCountdownFinished, setIsCountdownFinished] = useState(false);
  const [modalNotSkip, setModalNotSkip] = useState(false);
  const [modalNext, setModalNext] = useState(false);

  useEffect(() => {
    if (isCountdownFinished) setModalNext(true);
  }, [isCountdownFinished]);

  const filteredStepsOrder = useMemo(() => {
    if (mockTestType === MOCK_TEST_TYPE.QUESTION_TEST) {
      return [];
    }
    if (mockTestType === MOCK_TEST_TYPE.SECTION_TEST) {
      return stepsOrder.filter((step) => step !== STEPS.DESCRIPTION && step !== STEPS.NOTE);
    }
    return stepsOrder;
  }, [mockTestType, stepsOrder]);

  const handleNext = () => {
    if (stepIndex === filteredStepsOrder.indexOf(STEPS.DESCRIPTION) && !isCountdownFinished) {
      setModalNotSkip(true);
      return;
    }
    if (stepIndex < filteredStepsOrder.length - 1) {
      setStepIndex(stepIndex + 1);
      setModalNext(false);
      setModalNotSkip(false);
      setIsCountdownFinished(false);
    } else {
      startMockTest();
    }
  };

  const currentStep = filteredStepsOrder[stepIndex];
  const StepComponent = stepsConfig[currentStep]?.Component;

  const startMockTest = async () => {
    try {
      if (!mockTestId) return;

      const { step, remainTime } = getInitialStepAndTime(mockTestType || '', mockTestSection || '');

      const payload: IMockTestUserStart = {
        mockTestId,
        remainTime,
        step,
      };
      await mockTestStart(payload);
      navigate(`/exam/mock-test/detail?id=${mockTestId}`);
    } catch (error) {
      notificationController.error({
        message: error,
      });
    }
  };

  return (
    <>
      {StepComponent && (
        <StepComponent type={mockTestType} section={mockTestSection} setIsCountdownFinished={setIsCountdownFinished} />
      )}
      <S.Footer>
        <S.Column>
          <Button type="primary" onClick={handleNext} size="small">
            {t('page.mockTest.button.next')}
          </Button>
        </S.Column>
      </S.Footer>

      <Modal
        title={t('page.mockTest.modal.title_skip')}
        visible={modalNotSkip}
        footer={[]}
        closable={false}
        maskClosable={true}
        onCancel={() => setModalNotSkip(false)}
      >
        {t('page.mockTest.modal.content_skip')}
      </Modal>

      <Modal
        title={t('page.mockTest.modal.title_timeUp')}
        visible={modalNext}
        footer={[
          <Button key="next" type="primary" onClick={handleNext}>
            {t('page.mockTest.button.next')}
          </Button>,
        ]}
        closable={false}
      >
        {t('page.mockTest.modal.content_timeUp')}
      </Modal>
    </>
  );
};
